import { useState } from "react";
import type { Project } from "@/api/spaces";
import {
  utilityButtonClass,
  verticalTooltipClass,
} from "@/components/panels/app-utility-control-styles";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { layoutMessages } from "@/i18n/messages/layout";
import { ProjectAvatar } from "@/components/ui/project-avatar";
import { isTopLogoOnboardingLocked } from "@/desktop/onboarding-top-logo-lock";
import { cn } from "@/lib/cn";
import {
  createProjectFromUser,
  selectProjectFromUser,
} from "@/lib/select-project-space";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useProjectStore } from "@/stores/project-store";

interface RailProjectSwitcherProps {
  onManageProjects: (project?: Project) => void;
}

/**
 * Left-rail project switcher (desktop only). Sits above the search button in
 * `DesktopRailUtilities`; mirrors the header's `SpaceSwitcher` for the rail
 * layout. Web keeps the header switcher, so this component is never rendered
 * there.
 */
export function RailProjectSwitcher({
  onManageProjects,
}: RailProjectSwitcherProps) {
  const t = useT(layoutMessages);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const projects = useProjectStore((s) => s.projects);
  const currentProject = useProjectStore((s) => s.currentProject);
  const onboardingStep = useOnboardingStore((s) => s.step);
  const onboardingStepHydrated = useOnboardingStore((s) => s.stepHydrated);
  const locked = isTopLogoOnboardingLocked({
    step: onboardingStep,
    stepHydrated: onboardingStepHydrated,
  });

  if (!currentProject) return null;

  const handleSelect = (project: Project) => {
    selectProjectFromUser(project);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const project = await createProjectFromUser("rail-project-create");
      if (project) onManageProjects(project);
    } finally {
      setCreating(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(utilityButtonClass, "group relative")}
          disabled={locked}
          data-testid="rail-project-switcher"
          aria-label={`Project: ${currentProject.name}`}
        >
          <ProjectAvatar
            name={currentProject.name}
            color={currentProject.color}
            isHome={Boolean(currentProject.is_home)}
            shape="circle"
          />
          <span
            className={verticalTooltipClass}
            data-testid="rail-project-tooltip"
            data-suppressed={String(open)}
            aria-hidden={open || undefined}
            inert={open || undefined}
          >
            {currentProject.name}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="right"
        align="start"
        sideOffset={8}
        className="max-h-96 min-w-52 max-w-72 overflow-y-auto"
        data-testid="rail-project-menu"
      >
        {projects.map((project) => (
          <DropdownMenuItem
            key={project.id}
            icon={
              <ProjectAvatar
                name={project.name}
                color={project.color}
                isHome={Boolean(project.is_home)}
                size="sm"
                shape="circle"
              />
            }
            trailing={
              project.id === currentProject.id ? (
                <Icons.Check className="h-3.5 w-3.5" />
              ) : undefined
            }
            onClick={() => handleSelect(project)}
            data-testid={`rail-project-item-${project.id}`}
          >
            {project.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<Icons.Plus className="h-3.5 w-3.5" />}
          disabled={creating}
          onClick={() => void handleCreate()}
          data-testid="rail-project-new"
        >
          {t("newProject")}
        </DropdownMenuItem>
        <DropdownMenuItem
          icon={<Icons.Settings className="h-3.5 w-3.5" />}
          onClick={() => onManageProjects()}
          data-testid="rail-project-manage"
        >
          {t("manageProjects")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export type { RailProjectSwitcherProps };
